import axios from 'axios';
import { getApiBaseUrl } from './api';
import { getToken } from './auth';

const api = axios.create({
  baseURL: `${getApiBaseUrl()}/football`,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    config.baseURL = `${getApiBaseUrl()}/football`;
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Generic pagination params
interface PaginationParams {
  page?: number;
  page_size?: number;
  search?: string;
  [key: string]: any;
}

export const leagueApi = {
  getAll: (params?: PaginationParams) => api.get('/leagues/', { params }),
  getById: (id: number | string) => api.get(`/leagues/${id}/`),
  create: (data: any) => api.post('/leagues/', data),
  update: (id: number | string, data: any) => api.put(`/leagues/${id}/`, data),
  delete: (id: number | string) => api.delete(`/leagues/${id}/`),
};

export const playerApi = {
  getAll: (params?: PaginationParams) => api.get('/players/', { params }),
  getById: (id: number | string) => api.get(`/players/${id}/`),
  create: (data: any) => api.post('/players/', data),
  update: (id: number | string, data: any) => api.put(`/players/${id}/`, data),
  delete: (id: number | string) => api.delete(`/players/${id}/`),
  refreshStats: (id: number | string) => api.get(`/players/${id}/refresh_stats/`),
};

export const teamApi = {
  getAll: (params?: PaginationParams) => api.get('/teams/', { params }),
  getById: (id: number | string) => api.get(`/teams/${id}/`),
  create: (data: any) => api.post('/teams/', data),
  update: (id: number | string, data: any) => api.put(`/teams/${id}/`, data),
  delete: (id: number | string) => api.delete(`/teams/${id}/`),
};

export const stadiumApi = {
  getAll: (params?: PaginationParams) => api.get('/stadiums/', { params }),
  getById: (id: number | string) => api.get(`/stadiums/${id}/`),
  create: (data: any) => api.post('/stadiums/', data),
  update: (id: number | string, data: any) => api.put(`/stadiums/${id}/`, data),
  delete: (id: number | string) => api.delete(`/stadiums/${id}/`),
};

export const refereeApi = {
  getAll: (params?: PaginationParams) => api.get('/referees/', { params }),
  getById: (id: number | string) => api.get(`/referees/${id}/`),
  create: (data: any) => api.post('/referees/', data),
  update: (id: number | string, data: any) => api.put(`/referees/${id}/`, data),
  delete: (id: number | string) => api.delete(`/referees/${id}/`),
};

export const matchApi = {
  getAll: (params?: PaginationParams) => api.get('/matches/', { params }),
  getById: (id: number | string) => api.get(`/matches/${id}/`),
  create: (data: any) => api.post('/matches/', data),
  update: (id: number | string, data: any) => api.put(`/matches/${id}/`, data),
  delete: (id: number | string) => api.delete(`/matches/${id}/`),
};

export const statApi = {
  getAll: (params?: PaginationParams) => api.get('/stats/', { params }),
  getById: (id: number | string) => api.get(`/stats/${id}/`),
  create: (data: any) => api.post('/stats/', data),
  update: (id: number | string, data: any) => api.put(`/stats/${id}/`, data),
  delete: (id: number | string) => api.delete(`/stats/${id}/`),
};

export const eventApi = {
  getAll: (params?: PaginationParams) => api.get('/match-events/', { params }),
  getById: (id: number | string) => api.get(`/match-events/${id}/`),
  create: (data: any) => api.post('/match-events/', data),
  update: (id: number | string, data: any) => api.put(`/match-events/${id}/`, data),
  delete: (id: number | string) => api.delete(`/match-events/${id}/`),
};

export const matchPlayerApi = {
  getAll: (params?: PaginationParams) => api.get('/match-players/', { params }),
  getById: (id: number | string) => api.get(`/match-players/${id}/`),
  create: (data: any) => api.post('/match-players/', data),
  update: (id: number | string, data: any) => api.put(`/match-players/${id}/`, data),
  delete: (id: number | string) => api.delete(`/match-players/${id}/`),
};

export const squadPlayerApi = {
  getAll: (params?: PaginationParams) => api.get('/squad-players/', { params }),
  getById: (id: number | string) => api.get(`/squad-players/${id}/`),
  create: (data: any) => api.post('/squad-players/', data),
  update: (id: number | string, data: any) => api.put(`/squad-players/${id}/`, data),
  delete: (id: number | string) => api.delete(`/squad-players/${id}/`),
};

export const squadApi = {
  getAll: (params?: PaginationParams) => api.get('/squads/', { params }),
  getById: (id: number | string) => api.get(`/squads/${id}/`),
  create: (data: any) => api.post('/squads/', data),
  update: (id: number | string, data: any) => api.put(`/squads/${id}/`, data),
  delete: (id: number | string) => api.delete(`/squads/${id}/`),
};

export default api; 